<template>
  <div class="p-6">
    <h1 class="text-2xl font-bold mb-4">Create New Survey</h1>

    <!-- Survey Title -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Survey Title</label>
      <input 
        v-model="survey.title" 
        type="text" 
        placeholder="Enter survey title"
        class="border rounded p-2 w-full"
      />
    </div> 
 
    <!-- Survey Description -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Description</label>
      <textarea
        v-model="survey.description"
        placeholder="Enter survey description"
        class="border rounded p-2 w-full"
        rows="3"
      ></textarea>
    </div>

    <!-- Network + User Group Selection -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium mb-1">Network</label>
        <select 
          v-model="survey.networkId"
          class="border rounded p-2 w-full"
        >
          <option disabled value="">Select network</option>
          <option v-for="n in networks" :key="n.id" :value="n.id">
            {{ n.name }}
          </option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">User Group</label>
        <select 
          v-model="survey.groupId"
          class="border rounded p-2 w-full"
        >
          <option disabled value="">Select user group</option>
          <option v-for="g in userGroups" :key="g.id" :value="g.id">
            {{ g.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Date Range -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium mb-1">Start Date</label>
        <input 
          v-model="survey.startDate" 
          type="date" 
          class="border rounded p-2 w-full"
        />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">End Date</label>
        <input 
          v-model="survey.endDate" 
          type="date" 
          class="border rounded p-2 w-full"
        />
      </div>
    </div>

    <!-- Add Question -->
    <div class="mb-6">
      <h2 class="text-lg font-semibold mb-2">Questions</h2>
      <button 
        @click="addQuestion"
        class="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
      >
        + Add Question
      </button>

      <div v-for="(q, index) in survey.questions" :key="index" class="mt-4 p-4 border rounded bg-gray-50">
        <input 
          v-model="q.text" 
          type="text" 
          placeholder="Enter question text"
          class="border rounded p-2 w-full mb-2"
        />

        <label class="block text-sm font-medium mb-1">Question Type</label>
        <select 
          v-model="q.type" 
          class="border rounded p-2 w-full mb-2"
        >
          <option value="text">Text Answer</option>
          <option value="multiple">Multiple Choice</option>
          <option value="rating">Rating Scale</option>
        </select>

        <!-- Options for Multiple Choice -->
        <div v-if="q.type === 'multiple'" class="mt-2">
          <label class="block text-sm font-medium mb-1">Options</label>
          <div v-for="(opt, optIndex) in q.options" :key="optIndex" class="flex gap-2 mb-2">
            <input 
              v-model="q.options[optIndex]" 
              type="text" 
              placeholder="Option text"
              class="border rounded p-2 w-full"
            />
            <button 
              @click="removeOption(index, optIndex)" 
              class="text-red-500"
            >
              ✕
            </button>
          </div>
          <button 
            @click="addOption(index)"
            class="text-teal-600 text-sm"
          >
            + Add Option
          </button>
        </div>

        <div v-if="q.type === 'rating'" class="mt-2 text-sm text-gray-600">
          Rating scale (1–5)
        </div>

        <button 
          @click="removeQuestion(index)" 
          class="text-red-600 text-sm mt-2"
        >
          Remove Question
        </button>
      </div>
    </div>

    <!-- Save Survey -->
    <button 
      @click="saveSurvey"
      class="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
    >
      Save Survey
    </button>
  </div>
</template>

<script>
import axios from "axios";
import GraphQl from "@/services/graphql.service";

export default {
  data() {
    return {
      survey: {
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        questions: [],
        networkId: "",
        groupId: "",
      },
      userGroups: [],
      networks: [],
      loading: false,
      error: null,
      searchString: '',
      recordsPerPage: 10,
      organization: Object,
      orgId: this.$store.getters.orgId,
      currentPage: 1,
    };
  },
  mounted() {
   // this.fetchNetworks();
   // this.fetchUserGroups();
    this.loadNetworks();
    this.loadGroups();
  },
  computed: {
    offset: function () {
      return (this.currentPage - 1) * this.recordsPerPage;
    }
  },
  methods: {
    async fetchNetworks() {
      try {
        const res = await axios.get("https://apiresources.brandfi.co.ke/api/v1/networks");
        this.networks = res.data.data || res.data;
      } catch (e) {
        console.error("Failed to load networks", e);
      }
    },
    async fetchUserGroups() {
      try {
        const res = await axios.get("https://apiresources.brandfi.co.ke/api/v1/usergroups");
        this.userGroups = res.data.data || res.data;
      } catch (e) {
        console.error("Failed to load user groups", e);
      }
    },
    addQuestion() {
      this.survey.questions.push({ text: "", type: "text", options: [] });
    },
    removeQuestion(index) {
      this.survey.questions.splice(index, 1);
    },
    addOption(questionIndex) {
      this.survey.questions[questionIndex].options.push("");
    },
    removeOption(questionIndex, optionIndex) {
      this.survey.questions[questionIndex].options.splice(optionIndex, 1);
    },
    async saveSurvey() {
      this.loading = true;
      this.error = null;

      try {
        if (!this.survey.networkId || !this.survey.groupId) {
          this.$root.$emit("alert_error", [
            "Please select both a network and user group."
          ]);
          this.loading = false;
          return;
        }

        if (this.survey.questions.length === 0) {
          this.$root.$emit("alert_error", [
            "Please add at least one question before saving."
          ]);
          this.loading = false;
          return;
        }

        if (new Date(this.survey.startDate) > new Date(this.survey.endDate)) {
          this.$root.$emit("alert_error", [
            "Start date cannot be later than End date!"
          ]);
          this.loading = false;
          return;
        }

        const payload = {
          title: this.survey.title,
          description: this.survey.description,
          start_date: this.survey.startDate,
          end_date: this.survey.endDate,
          network_id: this.survey.networkId,
          user_group_id: this.survey.groupId,
          questions: this.survey.questions.map(q => ({
            question_text: q.text,
            question_type: q.type,
            options: q.type === "multiple" ? q.options : null,
          })),
        };

        const response = await axios.post(
          "https://apiresources.brandfi.co.ke/api/v1/survey",
          payload
        );

        console.log(response);
        this.$root.$emit("alert_success", ["Survey created successfully!"]);
        this.survey = { title: "", description: "", startDate: "", endDate: "", questions: [], networkId: "", groupId: "" };
        this.$router.push({ name: "surveys" });
      } catch (err) {
        console.error(err);
        this.$root.$emit("alert_error", ["Failed to save survey, please try again"]);
        this.error = "Failed to save survey.";
      } finally {
        this.loading = false;
      }
    },
    loadNetworks(newPage) {
      this.currentPage = newPage || 1;
      this.loading = true
      let params = 'limit: ' + 10 + ', offset: ' + this.offset + ', order: "name asc", search: "' + this.searchString + '"';
      GraphQl.sendRequest('{organization(id:"' + this.$store.getters.orgId + '") ' +
          '{venuesCount(' + params + ')' +
          ' venues(' + params + ') ' +
          '{id name }}}')
          .then(response => {
            this.organization = response.organization;
            this.networks = response.organization.venues;
          })
          .finally(() => {
            this.loading = false;
          })
    },
    loadGroups(newPage) {
      this.currentPage = newPage || 1;
      this.loading = true
      // let params = 'limit: ' + this.recordsPerPage + ', ' +
      //     'offset: ' + this.offset + ', ' +
      //     'order: "created_at desc", ' +
      //     'search: "' + this.searchString + '"';
      
          const query = `
{
  customerGroupsCount(search: "${this.searchString}")
  customerGroups(
    limit: ${this.recordsPerPage},
    offset: ${this.offset},
    order: "created_at desc",
    search: "${this.searchString}"
  ) {
    id
    name
    description
    customersCount
    createdBy { first_name last_name id }
  }
}`;
GraphQl.sendRequest(query)
  .then(res => {
    this.records = res.customerGroups;
    this.recordsCount = res.customerGroupsCount;
    this.userGroups = res.customerGroups;
  })
  .finally(() => {
    this.loading = false;
  });

    }
  },
};
</script>
